import { PageSliderProps } from "./PageSlider.types";
import React from "react";
import { useClassNames } from "@grassrootly/rn-core/src/structure";

export default function PageSlider({
  slides,
  classNames = ["flex1"],
  initialPageIndex = 0,
  onPageChange,
  slider,
}: PageSliderProps) {
  const [activeIndex, setActiveIndex] = React.useState(initialPageIndex);
  const style = useClassNames(classNames);
  const [cursor, setCursor] = React.useState("-webkit-grab");
  const [isDragging, setIsDragging] = React.useState(false);
  const pagerRef = React.useRef<HTMLDivElement>(null);
  const [prevPageX, setPrevPageX] = React.useState(0);
  const [prevScrollLeft, setPrevScrollLeft] = React.useState(0);

  const onMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    setCursor("grabbing");
    setIsDragging(true);
    setPrevPageX(e.pageX);
    setPrevScrollLeft(pagerRef.current?.scrollLeft || 0);
  };

  const onMouseUp = () => {
    setCursor("-webkit-grab");
    setIsDragging(false);
    if (!pagerRef?.current) return;
    const i = getClosestIndex(pagerRef.current.clientWidth, pagerRef.current.scrollLeft);
    onPageSelected(i);
  };

  const onPageSelected = React.useCallback(
    (i: number) => {
      if (!pagerRef?.current) return;
      pagerRef.current.scrollTo({ left: i * pagerRef.current.clientWidth, behavior: "smooth" });
      setActiveIndex(i);
      if (onPageChange) onPageChange(i);
    },
    [onPageChange]
  );

  const getClosestIndex = (width: number, scrolled: number) => {
    return Math.round(scrolled / width);
  };

  React.useEffect(() => {
    if (slider) {
      slider.current = {
        ...(slider.current || {}),
        setPage: (index: number) => {
          onPageSelected(index);
        },
      };
    }
  }, [slider]);

  return (
    <div
      style={{
        ...style,
        width: "100%",
        display: "flex",
        flexDirection: "row",
        cursor,
        overflow: "hidden",
      }}
      onMouseDown={onMouseDown}
      ref={pagerRef}
      onMouseUp={onMouseUp}
      onMouseLeave={onMouseUp}
      onMouseMove={(event) => {
        event.preventDefault();
        if (!isDragging) return;
        if (!pagerRef || !pagerRef.current) return;
        const pX = event.pageX - prevPageX;
        pagerRef.current.scrollLeft = prevScrollLeft - pX;
      }}
    >
      {slides.map((element, index) => (
        <div style={{ width: "100%", flex: "0 0 100%", display: "flex" }} key={index}>
          {element}
        </div>
      ))}
    </div>
  );
}
